/**
 * Country-aware phone validation + formatting.
 *
 * Wraps `libphonenumber-js` (Google's libphonenumber, ported to JS).
 * That library knows the per-country digit counts, national / inter-
 * national format rules, and country calling codes — everything our
 * own regex couldn't possibly handle.
 *
 * Three things the rest of the app needs:
 *
 *   `validatePhone(value, country)`
 *     Returns null if the value is acceptable for the country, or a
 *     short error string. Empty value → null (cleared field). Missing
 *     country → fall back to a loose digits/separators check so the
 *     field is still usable when the country picker hasn't been set.
 *
 *   `formatPhoneAsYouType(value, country)`
 *     Returns the value formatted progressively, the way you'd see in
 *     a phone-input field: "5" → "5" → "55" → "(555)" → "(555) 100-" →
 *     "(555) 100-2000". Used in the onChange handler so the field
 *     reformats live.
 *
 *   `getCountryCallingCodePrefix(country)`
 *     "+1" for US, "+44" for GB, etc. Used to render a hint under the
 *     field ("format: +1 ###-###-####") so the user knows what they're
 *     expected to type.
 *
 * Country codes are ISO 3166-1 alpha-2 strings (matching what our
 * `SUPPORTED_COUNTRIES` table uses and what libphonenumber accepts).
 */
import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

// Loose fallback for when no country is selected. Keeps the field
// useful — we still reject obvious garbage like "abc" — without the
// pretence of knowing the right format.
const LOOSE_PHONE_RE = /^\+?[\d(][\d\s\-().]{5,38}$/;

function validatePhoneLoose(v: string): string | null {
  return LOOSE_PHONE_RE.test(v)
    ? null
    : 'digits only (with +, space, dash, parens or dot)';
}

export function validatePhone(
  value: string,
  country: CountryCode | null,
): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  if (!country) {
    return validatePhoneLoose(trimmed);
  }

  // `parsePhoneNumberFromString` is the safe (returns undefined on bad
  // input) variant; the unsafe one throws. We don't want a thrown
  // error here — empty input was already handled above.
  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (!parsed) {
    return `enter a valid phone number for ${country}`;
  }
  if (!parsed.isValid()) {
    // isValid() is the strict check — right country, right length,
    // right area code, etc.
    return `not a valid ${country} phone number`;
  }
  return null;
}

export function formatPhoneAsYouType(
  value: string,
  country: CountryCode | null,
): string {
  if (!country) return value;
  const formatter = new AsYouType(country);
  // `AsYouType.input()` returns the formatted string for everything
  // typed so far. (Older versions exposed a `formattedOutput`
  // property as well; newer typings only ship the method signature,
  // so we use the documented return value.)
  const formatted = formatter.input(value);
  return formatted || value;
}

export function getCountryCallingCodePrefix(country: CountryCode | null): string {
  if (!country) return '';
  try {
    return `+${getCountryCallingCode(country)}`;
  } catch {
    // `getCountryCallingCode` throws on unknown country codes; we
    // never expect to hit this because our SUPPORTED_COUNTRIES list
    // is curated, but the safety net is cheap.
    return '';
  }
}

/**
 * Human-readable placeholder showing the expected phone shape for a
 * given country. Drives the TextField placeholder so the user sees
 * what to type before they start.
 *
 * We deliberately don't pull from libphonenumber-js's
 * `getExampleNumber` because that lives in a separate examples
 * data file (~150KB) — too much weight for what's a one-line UX
 * hint per country. The curated list below covers every entry in
 * SUPPORTED_COUNTRIES; anything else falls back to the US shape.
 */
const PHONE_PLACEHOLDERS: Record<string, string> = {
  US: '+1 (555) 555-5555',
  CA: '+1 (555) 555-5555',
  GB: '+44 20 7946 0958',
  AU: '+61 2 1234 5678',
  AT: '+43 1 1234567',
  BE: '+32 2 123 45 67',
  BR: '+55 11 12345-6789',
  CZ: '+420 123 456 789',
  DK: '+45 12 34 56 78',
  FI: '+358 50 1234567',
  FR: '+33 1 23 45 67 89',
  DE: '+49 30 12345678',
  HR: '+385 1 1234 567',
  HU: '+36 1 123 4567',
  IT: '+39 02 1234 5678',
  IN: '+91 22 1234 5678',
  IE: '+353 1 234 5678',
  JP: '+81 3-1234-5678',
  MX: '+52 55 1234 5678',
  NL: '+31 20 123 4567',
  NZ: '+64 9 123 4567',
  NO: '+47 12 34 56 78',
  PH: '+63 2 1234 5678',
  PL: '+48 12 345 67 89',
  PT: '+351 21 123 4567',
  ES: '+34 91 123 45 67',
  SE: '+46 8 123 45 67',
  CH: '+41 44 123 45 67',
  AR: '+54 11 1234-5678',
  DO: '+1 (809) 555-5555',
};

export function getPhonePlaceholder(country: CountryCode | null): string {
  if (!country) return '+1 (555) 555-5555';
  return PHONE_PLACEHOLDERS[country] ?? '+1 (555) 555-5555';
}

/**
 * Type-narrow helper — libphonenumber's `CountryCode` is a union of
 * 240+ string literals. Our `findCountry` returns `Country | null`
 * with a generic `string` code; this asserts that string into the
 * libphonenumber type so the call sites stay tidy.
 */
export function asCountryCode(code: string | null | undefined): CountryCode | null {
  if (!code) return null;
  return code as CountryCode;
}
